import React from "react";
import { IconPencil } from "@tabler/icons-react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import Tooltip from "components/Tooltip";
import { buildingStore } from "store/Building/BuildingStore";
import { CertificationEnum } from "utils";
import { buildingSettingsStore } from "features/Building/BuildingSettings/BuildingSettingsStore";
import { useComplianceInfo } from "hooks/useComplianceInfo";
import { useCertificationLicense } from "hooks/useCertificationLicense";
import useToast from "hooks/useToast";
import { conformityStore } from "../../../../store/ConformityStore.ts";

const CertificationSwitcher = () => {
  const { t } = useTranslation();
  const { getCurrentCertification } = useComplianceInfo();
  const { enabledKeys } = useCertificationLicense();
  const toast = useToast();

  const tooltips: { [key: string]: string } = {
    A1: t("building.A1Tooltip"),
    A2: t("building.A2Tooltip"),
  };

  const conformities = [
    ...conformityStore.conformityOptions
      .filter((rec) => rec.name != "Sonstige")
      .map((c) => ({
        id: c.id,
        name: `DIN EN 15978 (${c.name})`,
        tooltip: tooltips[c.name],
        activ: buildingStore.currentBuilding?.conformity_id === c.id,
      })),
  ].sort((a, b) => a.name.localeCompare(b.name));

  async function onChangeCertification(certificate: string) {
    const newCert = conformityStore.certificationOptions.find(
      (rec) => rec.name === certificate
    );

    await buildingSettingsStore.fetchUpdateBuilding({
      certification_id: newCert?.id,
      conformity_id: newCert?.conformities[0].id,
    });
  }

  async function onChangeConformity(conformityID: string) {
    await buildingSettingsStore.fetchUpdateBuilding({
      conformity_id: conformityID,
      certification_id: conformityStore.certificationOptions.find(
        (rec) => rec.name === "Keine"
      )?.id,
    });
  }

  // Tier 1 ∩ Tier 2: only certs entitled by the license AND enabled in settings.
  const activeCertifications = enabledKeys;

  function noCertificationActive() {
    return (
      <div className="p-4">
        <div className="text-xs font-normal text-gray-500 mb-4">
          {t("building.noCertificationActiveDesc")}
        </div>
        <div
          className="text-xs font-semibold text-indigo-700 flex justify-center cursor-pointer"
          onClick={handleOpenBuildingSettings}
        >
          {t("building.addCertification")}
        </div>
      </div>
    );
  }

  function certificationList() {
    return activeCertifications.map((item) => (
      <div
        key={item}
        className={clsx(
          "text-gray-700 truncate flex items-center justify-between transition",
          "cursor-pointer px-4 py-2 hover:bg-indigo-50 hover:text-indigo-700 group/certifications"
        )}
        onClick={() => onChangeCertification(CertificationEnum[item])}
      >
        <div className="text-sm font-medium truncate mr-2">
          {CertificationEnum[item]}
        </div>
        {getCurrentCertification()?.name?.toLowerCase() ===
          CertificationEnum[item].toLowerCase() && (
          <div className="text-sm text-gray-500  group-hover/certifications:text-indigo-500">
            {t("commons.active")}
          </div>
        )}
      </div>
    ));
  }

  function handleOpenBuildingSettings() {
    buildingSettingsStore.setOpenCertificationsTab(true);
    buildingSettingsStore.setOpenEditBuilding(true);
  }

  React.useEffect(() => {
    buildingSettingsStore.buildingAlreadyUpdated &&
      toast(t("building.successEditCertification"), "success");
  }, [buildingStore.currentBuilding?.certification_id]);

  return (
    <div className="max-w-[320px]">
      <div
        className={clsx(
          "flex truncate justify-betweenbg-gray-50 px-4 py-1 text-gray-500 border-b",
          "border-gray-300 items-center bg-gray-50"
        )}
      >
        <div className="text-xs font-medium text-gray-500 truncate mr-2">
          {t("building.calculateAccordingToCert")}
        </div>
        <IconPencil
          className="text-indigo-700 cursor-pointer"
          stroke={3}
          size={16}
          onClick={handleOpenBuildingSettings}
        />
      </div>
      {activeCertifications.length
        ? certificationList()
        : noCertificationActive()}
      <div
        className={clsx(
          "flex truncate justify-between bg-gray-50 px-4 py-1 text-gray-500 border-y",
          "border-gray-300 items-center bg-gray-50"
        )}
      >
        <div className="text-xs font-medium text-gray-500 truncate mr-2">
          {t("building.calculateAccordingToStandard")}
        </div>
      </div>
      <div>
        {conformities.map((c) => (
          <div
            key={c.id}
            className={clsx(
              "text-sm font-medium text-gray-700 truncate transition",
              "cursor-pointer px-4 py-2 flex items-center justify-between",
              "hover:bg-indigo-50 hover:text-indigo-700 group/conformities"
            )}
            onClick={() => onChangeConformity(c.id)}
          >
            <span>{c.name}</span>
            <div
              className={clsx(
                "flex text-sm font-normal items-center text-gray-500 group-hover/conformities:text-indigo-500"
              )}
            >
              {c.activ &&
                getCurrentCertification()?.name === "Keine" &&
                t("commons.active")}
              <Tooltip content={c.tooltip} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default observer(CertificationSwitcher);
